
import Vue from "vue";

import Dialog from "@/components/layout/Dialog.vue";
import TicketCard from "@/inspection/ticket/widgets/TicketCard.vue";
import { ChecklistEntry } from "@/inspection/shared/models/ChecklistEntry";
import { Ticket } from "@/inspection/shared/models/Ticket";
import IconButton from "@/components/basic/IconButton.vue";
import Textfield from "@/components/form/Textfield.vue";

interface TicketTreeItem {
  key: string;
  type: "entry" | "ticket" | "detail";
  label: string;
  ticketId?: number;
  ticket?: Ticket;
  ticketIds: number[];
  children?: TicketTreeItem[];
}

export default Vue.extend({
  components: {
    Dialog,
    TicketCard,
    Textfield
  },
  props: {
    value: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    error: {
      type: String,
      default: ""
    },
    checklistEntries: {
      type: Array,
      default: () => []
    },
    hiddenTicketIds: {
      type: Array,
      default: () => []
    },
    setHiddenTicketIds: {
      type: Function,
      required: true
    }
  },
  data(): { pendingHiddenTicketIds: number[]; searchQuery: string } {
    return {
      pendingHiddenTicketIds: [],
      searchQuery: ""
    };
  },
  computed: {
    dialogValue: {
      get(): boolean {
        return this.value as boolean;
      },
      set(value: boolean) {
        this.$emit("input", value);
      }
    },
    ticketTreeItems(): TicketTreeItem[] {
      return this.buildEntryItems(
        (this.checklistEntries as ChecklistEntry[]) ?? [],
        "root"
      );
    },
    filteredTicketTreeItems(): TicketTreeItem[] {
      const normalizedQuery = this.normalizeSearch(this.searchQuery);
      if (!normalizedQuery) {
        return this.ticketTreeItems;
      }

      return this.buildEntryItemsFiltered(
        (this.checklistEntries as ChecklistEntry[]) ?? [],
        "root",
        normalizedQuery
      );
    }
  },
  methods: {
    syncPendingHiddenTickets() {
      this.pendingHiddenTicketIds = [...(this.hiddenTicketIds as number[])];
    },
    pruneHiddenTickets() {
      const availableIds = new Set(this.collectTicketIdsFromEntries(
        (this.checklistEntries as ChecklistEntry[]) ?? []
      ));
      const nextHiddenIds = this.pendingHiddenTicketIds.filter(id =>
        availableIds.has(id)
      );

      if (nextHiddenIds.length !== this.pendingHiddenTicketIds.length) {
        this.pendingHiddenTicketIds = [...nextHiddenIds];
        this.setHiddenTicketIds([...nextHiddenIds]);
      }
    },
    applyChanges() {
      this.setHiddenTicketIds([...this.pendingHiddenTicketIds]);
      this.dialogValue = false;
    },
    cancelChanges() {
      this.syncPendingHiddenTickets();
      this.dialogValue = false;
    },
    buildEntryItems(entries: ChecklistEntry[], path: string): TicketTreeItem[] {
      return entries.map((entry, index) => {
        const entryKey = `${path}-entry-${entry.id ?? index}`;
        const entryTicketIds = this.collectTicketIds(entry);
        const childEntries = this.buildEntryItems(entry.entries ?? [], entryKey);
        const ticketItems = (entry.tickets ?? []).map((ticket, ticketIndex) =>
          this.buildTicketItem(ticket, entryKey, ticketIndex)
        );

        return {
          key: entryKey,
          type: "entry",
          label: entry.name ?? "Checklistenpunkt",
          ticketIds: entryTicketIds,
          children: [...childEntries, ...ticketItems]
        };
      });
    },
    buildEntryItemsFiltered(
      entries: ChecklistEntry[],
      path: string,
      query: string
    ): TicketTreeItem[] {
      return entries.reduce<TicketTreeItem[]>((items, entry, index) => {
          const entryKey = `${path}-entry-${entry.id ?? index}`;
          const entryMatches = this.matchesEntry(entry, query);
          const childEntries = this.buildEntryItemsFiltered(
            entry.entries ?? [],
            entryKey,
            query
          );
          const ticketItems = (entry.tickets ?? [])
            .filter(ticket => this.matchesTicket(ticket, query))
            .map((ticket, ticketIndex) =>
              this.buildTicketItem(ticket, entryKey, ticketIndex)
            );
          const children = [...childEntries, ...ticketItems];

          if (!entryMatches && children.length === 0) {
            return items;
          }

          items.push({
            key: entryKey,
            type: "entry",
            label: entry.name ?? "Checklistenpunkt",
            ticketIds: this.collectTicketIdsFromItems(children),
            children
          });

          return items;
        }, []);
    },
    buildTicketItem(ticket: Ticket, path: string, index: number): TicketTreeItem {
      const ticketId = ticket.id ?? 0;
      const ticketKey = `${path}-ticket-${ticketId || index}`;
      return {
        key: ticketKey,
        type: "ticket",
        label: ticket.title ?? "Ticket",
        ticketId,
        ticket,
        ticketIds: ticketId ? [ticketId] : []
      };
    },
    collectTicketIds(entry: ChecklistEntry): number[] {
      const currentTickets =
        entry.tickets?.map(ticket => ticket.id).filter(Boolean) ?? [];
      const childTickets = (entry.entries ?? []).flatMap(childEntry =>
        this.collectTicketIds(childEntry)
      );

      return [...currentTickets, ...childTickets] as number[];
    },
    collectTicketIdsFromEntries(entries: ChecklistEntry[]): number[] {
      return entries.flatMap(entry => this.collectTicketIds(entry));
    },
    collectTicketIdsFromItems(items: TicketTreeItem[]): number[] {
      return items.flatMap(item => {
        if (item.type === "ticket") {
          return item.ticketIds ?? [];
        }

        return this.collectTicketIdsFromItems(item.children ?? []);
      });
    },
    normalizeSearch(value: string) {
      return value.trim().toLowerCase();
    },
    matchesEntry(entry: ChecklistEntry, query: string) {
      const label = `${entry.name ?? ""} ${entry.description ?? ""}`;
      return label.toLowerCase().includes(query);
    },
    matchesTicket(ticket: Ticket, query: string) {
      const text = `${ticket.title ?? ""} ${ticket.description ?? ""}`;
      return text.toLowerCase().includes(query);
    },
    isTicketHidden(ticketId?: number) {
      if (!ticketId) {
        return false;
      }

      return this.pendingHiddenTicketIds.includes(ticketId);
    },
    isItemChecked(item: TicketTreeItem): boolean {
      if (item.type === "detail") {
        return false;
      }

      if (item.type === "ticket") {
        return !this.isTicketHidden(item.ticketId);
      }

      const total = item.ticketIds.length;
      if (total === 0) {
        return false;
      }

      const hidden = item.ticketIds.filter(id =>
        this.pendingHiddenTicketIds.includes(id)
      ).length;

      return hidden === 0;
    },
    isItemIndeterminate(item: TicketTreeItem): boolean {
      if (item.type !== "entry") {
        return false;
      }

      const total = item.ticketIds.length;
      if (total === 0) {
        return false;
      }

      const hidden = item.ticketIds.filter(id =>
        this.pendingHiddenTicketIds.includes(id)
      ).length;

      return hidden > 0 && hidden < total;
    },
    toggleItem(item: TicketTreeItem, value: boolean) {
      if (item.type === "detail") {
        return;
      }

      const hiddenSet = new Set(this.pendingHiddenTicketIds);

      if (item.type === "ticket") {
        if (!item.ticketId) {
          return;
        }

        if (value) {
          hiddenSet.delete(item.ticketId);
        } else {
          hiddenSet.add(item.ticketId);
        }
      } else {
        item.ticketIds.forEach(id => {
          if (value) {
            hiddenSet.delete(id);
          } else {
            hiddenSet.add(id);
          }
        });
      }

      this.pendingHiddenTicketIds = Array.from(hiddenSet.values());
    }
  },
  watch: {
    value(isOpen: boolean) {
      if (isOpen) {
        this.syncPendingHiddenTickets();
        this.pruneHiddenTickets();
      }
    },
    hiddenTicketIds() {
      if (!this.value) {
        this.syncPendingHiddenTickets();
      }
    },
    checklistEntries: {
      deep: true,
      handler() {
        if (this.value) {
          this.pruneHiddenTickets();
        }
      }
    }
  }
});
